/**
 * The session drawer's model: pure, so it is unit tested. What a person may do with a
 * session (origin, claim, role), how the Events tab filters, how the scrubber replays, and
 * what the Cost tab shows from state.ext.costs.
 */
import { SESSION_EVENT_KINDS, sessionOrigin } from '@orgus/protocol';
import type {
  CostsState,
  DiffFile,
  DiffLine,
  Session,
  SessionClaim,
  SessionEdit,
  SessionEvent,
  SessionEventKind,
  SessionEventsPage,
  SessionInputStatus,
  SessionMessage,
  SessionOrigin,
  SessionUsage,
} from '@orgus/protocol';

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export type DrawerTab = 'transcript' | 'diff' | 'events' | 'cost';
export const DRAWER_TABS: readonly { id: DrawerTab; label: string }[] = [
  { id: 'transcript', label: 'Transcript' },
  { id: 'diff', label: 'Diff' },
  { id: 'events', label: 'Events' },
  { id: 'cost', label: 'Cost' },
];

export function parseTab(v: string | null | undefined): DrawerTab {
  return DRAWER_TABS.some((t) => t.id === v) ? (v as DrawerTab) : 'transcript';
}

/** The drawer is wider on the Diff tab, widest side by side (px, its max width). */
export function drawerWidthFor(tab: DrawerTab, diffMode: 'unified' | 'split'): number {
  if (tab !== 'diff') return 680;
  return diffMode === 'split' ? 1120 : 880;
}

// ---------------------------------------------------------------------------
// Control: discovered vs launched, claims
// ---------------------------------------------------------------------------

export interface Control {
  origin: SessionOrigin;
  claim: SessionClaim | null;
  /** Discovered and not claimed: Orgus only watches it. */
  readOnly: boolean;
  /** The Claim action is offered (discovered, not claimed, still known). */
  canClaim: boolean;
  /** Why Claim is unavailable to this person, when it is offered but not allowed. */
  claimBlocked: string | null;
  /** The current person holds the claim, or may release it (admin+). */
  canRelease: boolean;
  /** Stop is offered: launched or claimed, live, with a process. */
  stopBlocked: string | null;
  /** The composer is shown (launched sessions only). */
  showComposer: boolean;
}

const OPERATOR = new Set(['owner', 'admin', 'operator']);
const ADMIN = new Set(['owner', 'admin']);

export function sessionControl(
  s: Pick<Session, 'origin' | 'status' | 'pid'>,
  claim: SessionClaim | null | undefined,
  me: { id: string; role: string },
  machine: { online: boolean; name: string } | null,
  /**
   * Other ways Orgus already holds a discovered session: an owner in teams (its Claim in
   * the drawer header), or session control on its machine (ADR 0001, amended). Either one
   * lifts the read-only watch, as a claim here does.
   */
  held: { owner?: string | null; controlled?: boolean } = {},
): Control {
  const origin = sessionOrigin(s);
  const c: SessionClaim | null = claim ?? (held.owner ? { sessionId: '', machineId: '', by: held.owner, at: 0, auditId: '' } : null);
  const readOnly = origin === 'discovered' && !c && !held.controlled;
  const operator = OPERATOR.has(me.role);
  const machineName = machine?.name ?? 'The machine';
  let stopBlocked: string | null = null;
  if (readOnly) stopBlocked = 'Orgus only watches this session. Claim it (beside its status) to stop it from here.';
  else if (!operator) stopBlocked = 'Your role cannot stop sessions.';
  else if (!machine?.online) stopBlocked = `${machineName} is offline.`;
  else if (!s.pid) stopBlocked = 'No process to stop.';
  return {
    origin,
    claim: c,
    readOnly,
    canClaim: origin === 'discovered' && !c,
    claimBlocked: operator ? null : 'Claiming needs the operator role or above.',
    canRelease: !!c && (c.by === me.id || ADMIN.has(me.role)),
    stopBlocked,
    showComposer: origin === 'launched',
  };
}

/** Whether the composer can send now, and the sentence to show when not. */
export function composerState(
  input: SessionInputStatus | null | undefined,
  opts: { loading: boolean; online: boolean; machineName: string; role: string },
): { canSend: boolean; hint: string } {
  if (!OPERATOR.has(opts.role)) return { canSend: false, hint: 'Your role can read this session but not send it messages.' };
  if (!opts.online) return { canSend: false, hint: `${opts.machineName} is offline, so messages cannot reach the session.` };
  if (opts.loading && !input) return { canSend: false, hint: 'Checking whether the session takes messages.' };
  if (!input) return { canSend: false, hint: 'Could not check whether the session takes messages.' };
  if (!input.accepting) return { canSend: false, hint: input.reason ?? 'The session does not take messages now.' };
  if (input.busy) return { canSend: true, hint: 'Claude is working. Your message is read when it finishes the current turn.' };
  return { canSend: true, hint: 'Claude reads your message as the next prompt.' };
}

/**
 * The one line at the top of a launched headless session's Activity tab: whether it takes
 * messages, with "Message this session" when it does, and what to do when it does not.
 */
export interface MessageEntryView {
  kind: 'checking' | 'open' | 'closed' | 'unknown';
  label: string;
  /** What to do next (closed), or null. */
  next: string | null;
  /** The machine's own reason, for the tooltip. */
  reason: string | null;
}

export function messageEntryView(input: SessionInputStatus | null | undefined, o: { loading: boolean; blocked: boolean }): MessageEntryView {
  if (!input) return o.loading ? { kind: 'checking', label: 'Checking whether it takes messages', next: null, reason: null } : { kind: 'unknown', label: 'Could not check whether it takes messages', next: null, reason: null };
  if (input.accepting) return { kind: 'open', label: input.busy ? 'Takes messages, read after this turn' : 'Takes messages', next: null, reason: null };
  return {
    kind: 'closed',
    label: 'Not taking messages',
    // An approval stopped it: Approve and resume above continues it, input or not.
    next: o.blocked ? 'Approve and resume above continues it.' : 'Start a new session to carry on.',
    reason: input.reason ?? null,
  };
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * The fetched history with the live slice on top: the slice is newer for state (sending,
 * sent, delivered), but its long texts are previews, so the fetched text wins.
 */
export function mergeMessages(fetched: readonly SessionMessage[], live: readonly SessionMessage[]): SessionMessage[] {
  const byId = new Map<string, SessionMessage>();
  for (const m of fetched) byId.set(m.id, m);
  for (const m of live) {
    const full = byId.get(m.id);
    if (m.clipped && full && !full.clipped) {
      const { clipped: _preview, ...rest } = m;
      byId.set(m.id, { ...rest, text: full.text });
    } else byId.set(m.id, m);
  }
  return [...byId.values()].sort((a, b) => a.at - b.at);
}

/** Ids of clipped live messages the fetched history lacks, as a key (empty when none). */
export function unseenClipped(fetched: readonly SessionMessage[] | undefined, live: readonly SessionMessage[]): string {
  // Before the first fetch there is nothing to compare with: that fetch brings them.
  if (!fetched) return '';
  const have = new Set((fetched ?? []).map((m) => m.id));
  return live
    .filter((m) => m.clipped && !have.has(m.id))
    .map((m) => m.id)
    .join(',');
}

/** Where the tab polls for events: only what changed since `cursor`, within `epoch`. */
export function eventsQuery(cursor: number, epoch: string | undefined): string {
  const q = new URLSearchParams();
  if (cursor) q.set('after', String(cursor));
  if (cursor && epoch) q.set('epoch', epoch);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * Whether a page replaces the list instead of merging into it: the first page, a cursor
 * that went backwards, or a new reader epoch (the agent renumbered the events, so the same
 * seq may now name a different event).
 */
export function replacesEvents(first: boolean, cursor: number, epoch: string | undefined, page: Pick<SessionEventsPage, 'last' | 'epoch'>): boolean {
  return first || page.last < cursor || (page.epoch !== undefined && page.epoch !== epoch);
}

/** Merge a page into what the tab has: by seq, a changed event replaces the old one. */
export function mergeEvents(prev: readonly SessionEvent[], page: Pick<SessionEventsPage, 'events'>, replace = false): SessionEvent[] {
  if (replace) return [...page.events];
  if (!page.events.length) return prev as SessionEvent[];
  const bySeq = new Map(prev.map((e) => [e.seq, e]));
  for (const e of page.events) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/** Minimal shape of state.ext.approvals (module 'approvals'), read loosely so it is optional. */
export interface ApprovalLike {
  id: string;
  sessionId: string;
  title: string;
  tool?: string;
  state: 'pending' | 'allowed' | 'denied' | 'expired';
  createdAt: number;
  decidedAt?: number;
  decidedBy?: string;
  reason?: string;
  kind?: string;
  fallback?: 'session';
  sessionAnswer?: { outcome: 'answered' | 'declined'; by: string | null; at: number };
}

/**
 * Approvals the approvals module holds for this session, as events. A question or plan passed
 * back to the session was not refused: it reads as asked there, or answered there (ADR 0056).
 */
export function approvalEvents(approvals: unknown, sessionId: string): SessionEvent[] {
  const a = approvals as { pending?: ApprovalLike[]; recent?: ApprovalLike[] } | undefined;
  if (!a) return [];
  const list = [...(a.pending ?? []), ...(a.recent ?? [])].filter((x) => x && x.sessionId === sessionId);
  return list.map((x, i) => {
    const base = { seq: -1 - i, at: x.createdAt, kind: 'approval' as const, title: x.title, ...(x.tool ? { tool: x.tool } : {}) };
    if (x.state === 'expired' && x.fallback === 'session' && (x.kind === 'question' || x.kind === 'plan')) {
      const sa = x.sessionAnswer;
      if (!sa) return { ...base, meta: 'Asked in the session' };
      const what = x.kind === 'plan' ? (sa.outcome === 'answered' ? 'Plan approved' : 'Plan sent back') : sa.outcome === 'answered' ? 'Answered' : 'Skipped';
      return { ...base, status: 'ok' as const, meta: `${what} in the session${sa.by ? ` by ${sa.by}` : ''}` };
    }
    return {
      ...base,
      status: x.state === 'pending' ? ('pending' as const) : x.state === 'allowed' ? ('allowed' as const) : ('denied' as const),
      ...(x.decidedBy ? { meta: x.state === 'expired' ? 'Timed out' : `Decided by ${x.decidedBy}` } : {}),
      ...(x.reason ? { detail: x.reason } : {}),
    };
  });
}

/** Transcript events plus module approvals, in time order (events without a time keep their place). */
export function combineEvents(events: readonly SessionEvent[], extra: readonly SessionEvent[]): SessionEvent[] {
  if (!extra.length) return events as SessionEvent[];
  return [...events, ...extra].sort((a, b) => (a.at ?? 0) - (b.at ?? 0) || a.seq - b.seq);
}

export type EventCounts = Record<SessionEventKind, number>;

export function eventCounts(events: readonly SessionEvent[]): EventCounts {
  const c = Object.fromEntries(SESSION_EVENT_KINDS.map((k) => [k, 0])) as EventCounts;
  for (const e of events) c[e.kind]++;
  return c;
}

export interface EventFilter {
  /** Kinds shown; empty means all. */
  kinds: readonly SessionEventKind[];
  query: string;
  /** Only failed, refused or still pending. */
  problems?: boolean;
}

export function filterEvents(events: readonly SessionEvent[], f: EventFilter): SessionEvent[] {
  const q = f.query.trim().toLowerCase();
  const kinds = new Set(f.kinds);
  return events.filter(
    (e) =>
      (!kinds.size || kinds.has(e.kind)) &&
      (!f.problems || e.kind === 'error' || e.status === 'error' || e.status === 'denied') &&
      (!q || `${e.title} ${e.detail ?? ''} ${e.tool ?? ''} ${e.meta ?? ''}`.toLowerCase().includes(q)),
  );
}

// ---------------------------------------------------------------------------
// Timeline scrubber (replay)
// ---------------------------------------------------------------------------

export interface Frame {
  index: number;
  event: SessionEvent;
  /** Totals up to and including this step. */
  prompts: number;
  tools: number;
  edits: number;
  errors: number;
  /** Files touched by edits so far, most recent first (max 8). */
  files: string[];
  /** Ms since the first step with a time. */
  elapsed: number | null;
}

/** One frame per event, with running totals, for replaying a session step by step. */
export function buildFrames(events: readonly SessionEvent[]): Frame[] {
  let prompts = 0;
  let tools = 0;
  let edits = 0;
  let errors = 0;
  let files: string[] = [];
  const t0 = events.find((e) => e.at != null)?.at ?? null;
  return events.map((e, index) => {
    if (e.kind === 'prompt') prompts++;
    if (e.kind === 'tool' || e.kind === 'subagent') tools++;
    if (e.kind === 'error' || e.status === 'error') errors++;
    if (e.edit) {
      edits++;
      files = [e.edit.file, ...files.filter((f) => f !== e.edit!.file)].slice(0, 8);
    }
    return { index, event: e, prompts, tools, edits, errors, files, elapsed: t0 != null && e.at != null ? e.at - t0 : null };
  });
}

export const REPLAY_SPEEDS = [1, 2, 4, 8] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/**
 * How long to show a step before the next one: the real gap between them divided by the
 * speed, held between 250 ms and 2.5 s so long pauses do not stall the replay.
 */
export function stepDelay(a: SessionEvent | undefined, b: SessionEvent | undefined, speed: ReplaySpeed): number {
  const gap = a?.at != null && b?.at != null ? Math.max(0, b.at - a.at) : 1000;
  return Math.round(Math.min(2500, Math.max(250, gap / speed / 10)));
}

/** Where on the track (0..1) each event sits: by time when known, else by order. */
export function trackPositions(events: readonly SessionEvent[]): number[] {
  const times = events.map((e) => e.at);
  const known = times.filter((t): t is number => t != null);
  const lo = known.length ? Math.min(...known) : 0;
  const hi = known.length ? Math.max(...known) : 0;
  const n = events.length;
  return events.map((e, i) => (hi > lo && e.at != null ? (e.at - lo) / (hi - lo) : n > 1 ? i / (n - 1) : 0));
}

// ---------------------------------------------------------------------------
// Cost
// ---------------------------------------------------------------------------

export interface CostView {
  usage: SessionUsage;
  /** Models by cost, highest first. */
  models: { model: string; cost: number; input: number; output: number; cacheRead: number; cacheWrite: number; share: number }[];
  /** Session-scoped budget, when one exists. */
  budget: { limit: number; spent: number; state: 'ok' | 'soft' | 'hard'; onLimit: 'warn' | 'stop' } | null;
  sourceLabel: string;
}

const SOURCE_LABEL: Record<SessionUsage['source'], string> = {
  telemetry: 'Measured by Claude Code telemetry',
  transcript: 'Estimated from the transcript token counts',
  billing: 'From the billing export',
  notional: 'Notional: a subscription seat, priced at API rates for comparison',
};

/** The Cost tab's data, or null when the costs module has nothing for this session. */
export function costView(costs: CostsState | undefined, sessionId: string): CostView | null {
  const usage = costs?.sessions.find((u) => u.sessionId === sessionId);
  if (!usage) return null;
  const total = usage.total.cost || 0;
  const models = Object.entries(usage.byModel)
    .map(([model, u]) => ({ model, ...u, share: total > 0 ? u.cost / total : 0 }))
    .sort((a, b) => b.cost - a.cost);
  const b = costs?.budgets.find((x) => x.scope.kind === 'session' && x.scope.sessionId === sessionId);
  const st = b ? costs?.budgetStatus.find((x) => x.budgetId === b.id) : undefined;
  return {
    usage,
    models,
    budget: b ? { limit: b.limit, spent: st?.spent ?? total, state: st?.state ?? 'ok', onLimit: b.onLimit } : null,
    sourceLabel: SOURCE_LABEL[usage.source] ?? usage.source,
  };
}

export function formatUsd(n: number): string {
  if (n > 0 && n < 0.01) return '< $0.01';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// An edit tool call as a one-hunk diff (the scrubber and the Events tab show it)
// ---------------------------------------------------------------------------

/** Lines are numbered from 1 on both sides: the transcript does not say where in the file the edit landed. */
export function editDiffFile(edit: SessionEdit): DiffFile {
  const split = (t: string) => (t ? t.replace(/\r\n/g, '\n').split('\n') : []);
  const before = split(edit.before);
  const after = split(edit.after);
  const lines: DiffLine[] = [
    ...before.map((text, i) => ({ kind: 'del' as const, text, old: i + 1, new: null })),
    ...after.map((text, i) => ({ kind: 'add' as const, text, old: null, new: i + 1 })),
  ];
  return {
    path: edit.file,
    oldPath: null,
    status: before.length ? 'modified' : 'added',
    binary: false,
    additions: after.length,
    deletions: before.length,
    hunks: lines.length
      ? [{ header: `@@ -1,${before.length} +1,${after.length} @@`, oldStart: 1, oldLines: before.length, newStart: 1, newLines: after.length, section: '', lines }]
      : [],
    truncated: edit.clipped ? 'lines' : null,
  };
}

// ---------------------------------------------------------------------------
// The model a session runs, and how to answer a session that waits
// ---------------------------------------------------------------------------

/**
 * The model a session runs: what it last reported, else the model that did most of its work
 * (its usage, subagents included), else null (it has not replied yet). Price variants
 * ("<model>#fast") count as their model.
 */
export function sessionModelOf(s: Pick<Session, 'model' | 'usage'>): string | null {
  if (s.model) return s.model;
  let best: string | null = null;
  let most = -1;
  for (const [key, t] of Object.entries(s.usage?.byModel ?? {})) {
    const model = key.split('#')[0] ?? key;
    const n = t.input + t.output + t.cacheRead + t.cacheWrite;
    if (model && model !== '<synthetic>' && n > most) {
      best = model;
      most = n;
    }
  }
  return best;
}

/**
 * The model a session Orgus launched was started with, from its log: the launcher's own
 * line ("Session <id> started (model <m>)") or Claude Code's stream-json init.
 */
export function launchModelOf(log: string): string | null {
  const line = /^Session \S+ started \(model ([^)\s]+)\)\s*$/m.exec(log);
  if (line?.[1]) return line[1];
  for (const raw of log.split('\n')) {
    if (!raw.includes('"init"')) continue;
    try {
      const j = JSON.parse(raw) as { type?: unknown; subtype?: unknown; model?: unknown };
      if (j.type === 'system' && j.subtype === 'init' && typeof j.model === 'string' && j.model) return j.model;
    } catch {
      // not a JSON line
    }
  }
  return null;
}

/** How a person answers a session that waits, from what Orgus can reach on it. */
export type AnswerRoute = 'approval' | 'terminal' | 'composer' | 'message' | 'watched';

export function answerRoute(o: { approvalPending: boolean; liveTerminal: boolean; composer: boolean; controlled: boolean }): AnswerRoute {
  if (o.approvalPending) return 'approval';
  if (o.liveTerminal) return 'terminal';
  if (o.composer) return 'composer';
  if (o.controlled) return 'message';
  return 'watched';
}

/** One sentence: where to answer it. */
export function answerText(route: AnswerRoute, machine: string): string {
  switch (route) {
    case 'approval':
      return 'Allow or deny its request below.';
    case 'terminal':
      return 'Answer it in the terminal Orgus started.';
    case 'composer':
      return 'Answer it with a message below.';
    case 'message':
      return 'Send it a message below.';
    case 'watched':
      return `Answer it in its terminal on ${machine}, or turn on session control for ${machine}.`;
  }
}

/**
 * The ask's message without the list of its own options: when the options show as quick-reply
 * buttons, a bullet list naming the same choices says them twice. Only list items whose text is
 * one of the options go; any other line stays.
 */
export function withoutOptionList(body: string, options: readonly string[]): string {
  if (!options.length) return body;
  const norm = (t: string) => t.replace(/[*_`]/g, '').replace(/[.:;,]+$/, '').trim().toLowerCase();
  const wanted = new Set(options.map(norm));
  const kept = body.split('\n').filter((line) => {
    const m = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/.exec(line);
    return !(m && wanted.has(norm(m[1] ?? '')));
  });
  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
